import { useNavigate } from "react-router-dom"
import { CheckCircle2, ArrowRight, HeartPulse, Shield, Lightbulb, Users, HandHeart } from "lucide-react"
import { Button } from "../../../components/ui/button"
import heroImage from "../../../assets/TopHeroImages/2.png"

export default function Medecine() {
    const navigate = useNavigate()
    return (
        <div className="min-h-screen bg-background font-sans text-foreground">
            {/* Hero Section */}
            <header className="relative min-h-[60vh] flex items-center bg-primary overflow-hidden">
                <div className="absolute inset-0 z-0">
                    {heroImage && <img src={heroImage} alt="Faculté de Médecine et des Sciences de la Santé" className="w-full h-full object-cover opacity-40 mix-blend-luminosity scale-105" />}
                    <div className="absolute inset-0 bg-gradient-to-r from-primary via-primary/80 to-transparent" />
                </div>
                <div className="container relative z-10 py-20">
                    <div className="max-w-3xl">
                        <div className="inline-flex items-center gap-4 mb-8 translate-y-4 animate-in fade-in duration-700 fill-mode-forwards">
                            <span className="text-[10px] uppercase tracking-[0.3em] text-accent font-black">Sciences de la Santé</span>
                            <div className="h-px w-12 bg-accent/50" />
                            <span className="text-[10px] text-primary-foreground/60 uppercase tracking-widest font-bold">Depuis 1999</span>
                        </div>
                        <h1 className="heading-1 mb-8 leading-[0.95] font-serif tracking-tight text-white translate-y-4 animate-in fade-in duration-1000 fill-mode-forwards delay-200">
                            <span className="block text-white">Faculté de Médecine et</span>
                            <span className="block italic font-light text-accent/90">Sciences de la Santé</span>
                        </h1>
                        <div className="border-l-4 border-accent pl-8 mb-12 max-w-2xl translate-y-4 animate-in fade-in duration-1000 fill-mode-forwards delay-500">
                            <p className="text-lg md:text-xl font-light leading-relaxed text-primary-foreground/90">
                                Une formation d'excellence alliant rigueur scientifique et humanisme.
                                <span className="italic block mt-1 tracking-tight">Accréditée par la Communauté Est-Africaine.</span>
                            </p>
                        </div>
                        <div className="flex flex-wrap gap-6 items-center translate-y-4 animate-in fade-in duration-1000 fill-mode-forwards delay-700">
                            <Button onClick={() => navigate("/admission")} className="bg-accent text-accent-foreground px-10 py-7 text-sm font-black transition-all duration-300 border-b-4 border-black/20 rounded-xl">
                                Candidater au Concours
                                <ArrowRight className="w-5 h-5 ml-3" />
                            </Button>
                            <Button onClick={() => document.getElementById("historique")?.scrollIntoView({ behavior: "smooth" })} variant="ghost" className="text-white border-2 border-white/20 hover:bg-white/10 hover:border-white px-10 py-7 text-sm font-black rounded-xl backdrop-blur-sm">
                                Découvrir la Faculté
                            </Button>
                        </div>
                    </div>
                </div>
                <div className="absolute bottom-10 left-1/2 -translate-x-1/2 animate-bounce opacity-20">
                    <div className="w-px h-12 bg-white" />
                </div>
            </header>

            {/* Stats */}
            <div className="border-y border-border bg-secondary/10">
                <div className="container py-4 flex flex-wrap items-center gap-8">
                    {[
                        { val: "6 Ans", label: "Médecine (LMD)" },
                        { val: ">900", label: "Médecins Formés" },
                        { val: ">1000", label: "Techniciens Paramédicaux" },
                        { val: "CHU", label: "HMK Accrédité" }
                    ].map((stat, i) => (
                        <div key={i} className="flex items-center gap-3">
                            <span className="text-xl font-black text-primary">{stat.val}</span>
                            <span className="text-[10px] text-muted-foreground uppercase font-black tracking-widest">{stat.label}</span>
                            {i < 3 && <span className="hidden lg:block text-border ml-4 font-light">/</span>}
                        </div>
                    ))}
                </div>
            </div>

            {/* Main Article */}
            <article className="container section-padding-sm">
                <div className="grid lg:grid-cols-[250px_1fr] gap-12 lg:gap-20">
                    {/* Sidebar */}
                    <aside className="lg:sticky lg:top-8 lg:self-start hidden lg:block">
                        <nav className="space-y-1">
                            <p className="text-[10px] uppercase tracking-[0.2em] font-black text-muted-foreground mb-6 pb-2 border-b border-border">
                                Sommaire
                            </p>
                            {[
                                { id: "historique", label: "Historique" },
                                { id: "vision", label: "Vision & Missions" },
                                { id: "valeurs", label: "Valeurs" },
                                { id: "admission", label: "Admission" },
                                { id: "enseignements", label: "Organisation" }
                            ].map((item, i) => (
                                <a key={i} href={`#${item.id}`} className="group flex items-start gap-3 py-2 text-xs font-bold text-muted-foreground hover:text-primary transition-smooth">
                                    <span className="text-accent font-black">0{i + 1}</span>
                                    {item.label}
                                </a>
                            ))}
                        </nav>

                        <div className="mt-12 pt-8 border-t border-border">
                            <div className="bg-primary/5 p-6 border-l-4 border-primary">
                                <h4 className="text-[10px] uppercase tracking-[0.2em] font-black mb-4 text-primary">En Bref</h4>
                                <dl className="space-y-4 text-[11px]">
                                    <div className="flex justify-between items-baseline border-b border-border/40 pb-2">
                                        <dt className="font-bold">Création</dt>
                                        <dd className="text-muted-foreground">1999</dd>
                                    </div>
                                    <div className="flex justify-between items-baseline border-b border-border/40 pb-2">
                                        <dt className="font-bold">Cursus</dt>
                                        <dd className="text-muted-foreground">LMD (6 ans)</dd>
                                    </div>
                                    <div className="flex justify-between items-baseline">
                                        <dt className="font-bold">Stages</dt>
                                        <dd className="text-muted-foreground text-right">CHU HMK<br />Bujumbura</dd>
                                    </div>
                                </dl>
                            </div>
                        </div>
                    </aside>

                    {/* Main Body */}
                    <main className="space-y-16">
                        
                        {/* Section 01 - Historique */}
                        <section id="historique">
                            <h2 className="heading-4 text-foreground mb-6 flex items-center gap-3">
                                <div className="w-1 h-6 bg-accent rounded-full"></div>
                                Historique
                            </h2>

                            <div className="prose-sm max-w-none space-y-4 text-muted-foreground font-medium leading-relaxed">
                                <p>
                                    La Faculté de Médecine et des Sciences de la Santé a été créée avec la naissance de l’Université de Ngozi par l’Assemblée Générale constituante des membres fondateurs en 1999. A cette époque, elle logeait dans la Faculté des Sciences et des Technologies.
                                </p>
                                <p>
                                    A son ouverture, l’Université comptait 415 étudiants dont 20 en Médecine et 48 à l’Institut Supérieur Paramédical. A ce jour, l’Université de Ngozi a déjà diplômé <strong>plus de 900 médecins</strong> et <strong>plus de 1000 techniciens supérieurs paramédicaux</strong>. Les premiers diplômes de la Faculté de Médecine ont été délivrés en 2008, après 7 ans de formation.
                                </p>
                                <p>
                                    En 2014, conformément à la loi sur l’Enseignement Supérieur, il fût adopté le système Licence, Maîtrise, Doctorat (LMD) où la formation de médecine s’étendait sur 6 ans. En décembre 2018, il y a eu fusion de la Faculté de Médecine et de l’Institut Supérieur des Sciences de la Santé et devint la Faculté de Médecine et des Sciences de la Santé.
                                </p>
                                <div className="bg-secondary/5 p-6 border-l-4 border-accent my-6">
                                    <h4 className="text-sm font-bold text-primary mb-2">Partenariats et Accréditations</h4>
                                    <p className="mb-2">
                                        Dans le cadre de la formation pratique, les stagiaires en médecine étaient affectés dans les hôpitaux nationaux de Bujumbura dont l’Hôpital Prince Régent Charles, la Clinique Prince Louis Rwagasore et l’Hôpital Militaire de Kamenge (HMK). 
                                    </p>
                                    <p className="mb-2">
                                        Le décret no100/194 du 24 décembre 2019 assigna à l’HMK entre autres missions celles de promouvoir la recherche et la formation dans le domaine de la santé. Une ordonnance ministérielle conjointe no630/610/520/997 du 03/02/2025 érigea l’HMK en <strong>Centre Hospitalo-Universitaire (CHU)</strong> pour l’Université de Ngozi. Cet hôpital avait déjà été accrédité par le COSECSA pour former des chirurgiens.
                                    </p>
                                    <p className="font-bold text-primary">
                                        En 2025, l’inspection conjointe des écoles de médecine par les experts de la Communauté Est-Africaine a accrédité la Faculté de médecine de l’Université de Ngozi et l’HMK comme des institutions de formation médicale.
                                    </p>
                                </div>
                            </div>
                        </section>

                        {/* Section 02 - Vision & Missions */}
                        <section id="vision">
                            <h2 className="heading-4 text-foreground mb-6 flex items-center gap-3">
                                <div className="w-1 h-6 bg-accent rounded-full"></div>
                                Vision et Objectif
                            </h2>

                            <div className="grid md:grid-cols-2 gap-6">
                                <div className="p-6 border border-border/50 rounded-lg bg-secondary/5">
                                    <h3 className="heading-5 mb-4 text-primary">Notre Vision</h3>
                                    <p className="text-sm text-muted-foreground leading-relaxed">
                                        Promouvoir l’excellence par rapport à l’Enseignement et à la Recherche dans le domaine des soins et des Services de Santé.
                                    </p>
                                </div>
                                <div className="p-6 border border-border/50 rounded-lg bg-secondary/5">
                                    <h3 className="heading-5 mb-4 text-primary">Notre Mission</h3>
                                    <p className="text-sm text-muted-foreground leading-relaxed">
                                        Participer activement à l’avancement du savoir et à la maitrise des compétences pour l’amélioration des soins de santé.
                                    </p>
                                </div>
                                <div className="p-6 border border-border/50 rounded-lg bg-secondary/5 md:col-span-2 border-l-4 border-l-accent">
                                    <h3 className="heading-5 mb-4 text-primary">Objectif Général</h3>
                                    <p className="text-sm text-muted-foreground leading-relaxed font-medium">
                                        Offrir une formation complète et de haute qualité en vue d’avoir de médecins capables de répondre aux besoins du moment de la population en matière de santé en se référant aux standards proposés par l’OMS.
                                    </p>
                                </div>
                            </div>
                        </section>

                        {/* Section 03 - Valeurs */}
                        <section id="valeurs">
                            <h2 className="heading-4 text-foreground mb-8 flex items-center gap-3">
                                <div className="w-1 h-6 bg-accent rounded-full"></div>
                                Philosophie et Valeurs Guides
                            </h2>

                            <div className="grid md:grid-cols-3 gap-4">
                                {[
                                    { icon: HeartPulse, label: "Respect de la vie" },
                                    { icon: Shield, label: "Intégrité" },
                                    { icon: Lightbulb, label: "Innovation" },
                                    { icon: Users, label: "Collaboration" },
                                    { icon: HandHeart, label: "Responsabilité sociale" },
                                ].map((valeur, i) => (
                                    <div key={i} className="flex flex-col items-center text-center gap-3 p-6 bg-secondary/5 border border-border/50 rounded-sm hover:-translate-y-1 transition-transform">
                                        <valeur.icon className="w-8 h-8 text-accent" />
                                        <span className="text-[12px] font-bold uppercase tracking-tight">{valeur.label}</span>
                                    </div>
                                ))}
                            </div>
                        </section>

                        {/* Section 04 - Admission */}
                        <section id="admission">
                            <h2 className="heading-4 text-foreground mb-6 flex items-center gap-3">
                                <div className="w-1 h-6 bg-accent rounded-full"></div>
                                Profil des Étudiants
                            </h2>

                            <div className="prose-sm max-w-none text-muted-foreground font-medium leading-relaxed bg-primary/5 p-6 rounded-lg border border-primary/20">
                                <p>
                                    Ce sont les jeunes lauréats du post-fondamentale qui remplissent les conditions exigées par l’autorité de tutelle pour être admis dans l’enseignement supérieur, en l’occurrence :
                                </p>
                                <ul className="list-disc pl-5 mt-4 space-y-2">
                                    <li>La réussite à l’Examen d’Etat</li>
                                    <li>La réussite au <strong>concours d’admission à la Faculté de médecine</strong> (comme pôle d’excellence) organisé par le Ministère de l’Education Nationale et de la Recherche Scientifique.</li>
                                </ul>
                            </div>
                        </section>

                        {/* Section 05 - Enseignements */}
                        <section id="enseignements">
                            <h2 className="heading-4 text-foreground mb-6 flex items-center gap-3">
                                <div className="w-1 h-6 bg-accent rounded-full"></div>
                                Organisation des Enseignements
                            </h2>

                            <div className="space-y-6">
                                <p className="text-sm text-muted-foreground font-medium mb-6">
                                    Le Département de Médecine est organisé en trois cycles qui s’étendent sur six ans :
                                </p>

                                <div className="grid gap-4 relative before:absolute before:inset-y-0 before:left-[15px] before:w-0.5 before:bg-border">
                                    {[
                                        { 
                                            cycle: "1er Cycle", 
                                            desc: "Propédeutique médicale.",
                                            years: "Début de formation"
                                        },
                                        { 
                                            cycle: "2ème Cycle", 
                                            desc: "Pathologies médicales en rappelant chaque fois à la physiopathologie.",
                                            years: "3ème à la 5ème année"
                                        },
                                        { 
                                            cycle: "3ème Cycle", 
                                            desc: "Largement consacrée aux stages hospitaliers.",
                                            years: "6ème année"
                                        }
                                    ].map((phase, i) => (
                                        <div key={i} className="relative pl-12">
                                            <div className="absolute left-0 top-1.5 w-8 h-8 bg-background border-2 border-primary rounded-full flex items-center justify-center">
                                                <div className="w-2 h-2 bg-accent rounded-full" />
                                            </div>
                                            <div className="bg-secondary/5 p-6 rounded-lg border border-border/50">
                                                <div className="flex items-center gap-3 mb-2">
                                                    <h3 className="font-bold text-primary">{phase.cycle}</h3>
                                                    <span className="text-[10px] px-2 py-1 bg-primary/10 rounded-full font-bold uppercase tracking-wider text-primary">
                                                        {phase.years}
                                                    </span>
                                                </div>
                                                <p className="text-sm text-muted-foreground">
                                                    {phase.desc}
                                                </p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </section>

                    </main>
                </div>
            </article>

            {/* Footer / CTA - preserved */}
            <footer className="bg-primary text-white border-t-8 border-accent">
                <div className="container py-16 text-center max-w-2xl">
                    <span className="text-[10px] uppercase tracking-[0.3em] text-accent font-black mb-4 block">Prochaine Étape</span>
                    <h2 className="heading-2 mb-8 leading-tight">
                        Soignez l'avenir.<br />Rejoignez-nous.
                    </h2>
                </div>
            </footer>
            
        </div>
    )
}
